import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {WHEN_NOT_IN_OPTIMIZE_MODE} from './optimization';

/**
 * Used to control the ability to modify a component via the On Canvas Editing experience.
 */
export const canModifyComponentsOnCanvas = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    workflows: {canvas: true},
    permission: ['modify', 'siteSymbol'],
    siteModes: {design: true},
    branchMergeLocked: false,
  },
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    workflows: {canvas: true},
    permission: ['modify', 'siteSymbol'],
    siteModes: {edit: true, design: true},
    featureFlags: {
      [FEATURE_FLAGS.REALTIME]: true,
    },
    branchMergeLocked: false,
  },
] as const);

/**
 * Used to control the ability to manage component definitions (rename, delete, duplicate, edit settings).
 * This is separate from canModifyComponentsOnCanvas which is used for canvas operations (focus into component, edit content).
 *
 * e.g. the 3 dot menu in the Components panel, or in the Finder
 *
 * When locales FGA is enabled, requires editContent access on the current locale
 * since component management operations modify the primary locale structure.
 */
export const canManageComponents = defineAbilityConfig([
  {
    abilities: {canEditLocaleContent: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    workflows: {canvas: true, dynamicData: true},
    permission: ['manage', 'siteSymbol'],
    siteModes: {design: true},
    branchMergeLocked: false,
  },
] as const);

/**
 * Used to control visibility of the Component variant's popover UI for managing
 * style variants.
 */
export const canManageStyleVariants = defineAbilityConfig({
  abilities: {canManageComponents: true, canModifyStyleBlocks: true},
} as const);

/**
 * Evaluates to `true` when the user is NOT editing a code component in any
 * mode (neither on the code component canvas nor focused in context).
 *
 * Due for removal once all consumers are migrated to use
 * `isEditingCodeComponent: false` directly.
 */
export const isNotEditingCodeComponent = defineAbilityConfig({
  isEditingCodeComponent: false,
} as const);

/**
 * Controls whether element renaming is allowed from the Navigator panel.
 */
export const canRenameElementInNavigator = defineAbilityConfig({
  isEditingCodeComponent: false,
} as const);

/**
 * Used to control access to CRUD operations on Component style variant
 * properties and options.
 *
 * When locales FGA is enabled, requires editContent access on the current locale
 * since variant modifications affect the primary locale component definition.
 */
export const canModifyStyleVariants = defineAbilityConfig({
  abilities: {
    canEditLocaleContent: true,
    canModifyComponentPropertyDataType: true,
    canModifyStyleBlocks: true,
  },
  isEditingCodeComponent: false,
} as const);

/**
 * Controls whether a user can add components to the canvas.
 * Typically the components panel visibility handles this, but use this ability
 * when the panel should remain visible while component addition is disabled.
 */
export const canAddComponentToCanvas = defineAbilityConfig({
  isEditingCodeComponent: false,
} as const);

/**
 * Used to control access to the Properties panel for editing prop defaults
 * and Style Variants on the main component definition while focused into a
 * component instance.
 *
 * Restricted to designer-class roles via `siteDesignerModes.design`. Marketer
 * and Content editor roles lack that permission, so even while focused into
 * a component from Build or Edit mode they cannot reach this panel.
 */
export const canAccessPropsManagementPanel = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
  },
  permission: ['design', 'siteDesignerModes'],
  siteModes: {design: true},
  isUserComponentFocused: true,
  workflows: {
    canvas: true,
    preview: true,
    comment: true,
    logicFlows: true,
    dynamicData: true,
    optimization: true,
    optimizationClickGoal: true,
    optimizationRedirectTest: true,
    analyzeVisualView: true,
  },
} as const);

/**
 * Used to guard generating suggested props.
 *
 * Suggested props imply that a user can then accept them,
 * so we only allow this in context where a user can apply them.
 */
export const canGenerateSuggestedProps = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: false,
  },
  localization: {
    currentLocaleType: {
      primary: true,
    },
  },
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
  siteModes: {design: true, edit: false},
  workflows: {
    canvas: true,
    preview: true,
    comment: false,
    logicFlows: false,
    optimization: false,
    optimizationClickGoal: false,
    optimizationRedirectTest: false,
    analyzeVisualView: false,
  },
} as const);

/**
 * Used to control access to Global component settings UI.
 *
 * This ability is also used during Designer bootstrap to determine
 * when global component settings initialization needs to be run.
 */
export const canAccessGlobalComponentSettings = defineAbilityConfig([
  {
    userTypes: {
      authorizedInDesigner: true,
    },
    permission: ['design', 'siteDesignerModes'],
    siteModes: {design: true},
    branchMergeLocked: false,
  },
] as const);
